import React, { useEffect, useRef, useState } from "react";
import { connect } from 'react-redux'
import * as maptilersdk from '@maptiler/sdk'
import { lineCreateRequest } from "../../../../action/line-actions";
import { collapseLeftToolbar } from "../../../../action/left-toolbar-state-actions";
import { photoUploadRequest } from "../../../../action/photo-actions";
import { MEASUREMENT_TYPES } from '../../../../lib/enums'
import elevation from "../../../../lib/api/elevation";
import toaster from '../../../main-ui/toast'

const PREVIEW_SRC = 'drawing-line-preview-src'
const PREVIEW_LAYER = 'drawing-line-preview-layer'

function addPreviewLayer(map, color) {
  if (map.getSource(PREVIEW_SRC)) return
  map.addSource(PREVIEW_SRC, {
    type: 'geojson',
    data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [] }, properties: {} }
  })
  map.addLayer({
    id: PREVIEW_LAYER,
    type: 'line',
    source: PREVIEW_SRC,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': color, 'line-width': 3, 'line-opacity': 0.85, 'line-dasharray': [2, 1] }
  })
}

function haversineMeters([lng1, lat1], [lng2, lat2]) {
  const R = 6371000
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}
function segmentsLength(coords) {
  let total = 0
  for (let i = 1; i < coords.length; i++) total += haversineMeters(coords[i - 1], coords[i])
  return total
}

const ThreeDLineDetails = (props) => {
  const [color, setColor] = useState('#0000FF')
  const [name, setName] = useState(`Line - ${props.lines.length + 1}`)
  const [notes, setnotes] = useState('')
  const [loading, setloading] = useState(false)

  const drawing = useRef(false)
  const activeCoords = useRef([])
  const markerRef = useRef(null)
  const colorRef = useRef(color)
  const infoPopupRef = useRef(null)
  const elevationCache = useRef({})
  const nameRef = useRef(name)
  const tokenRef = useRef(props.token)
  const unitsRef = useRef(props.userPreferences?.units || 'imperial')

  useEffect(() => { colorRef.current = color }, [color])
  useEffect(() => { nameRef.current = name }, [name])
  useEffect(() => { tokenRef.current = props.token }, [props.token])
  useEffect(() => { unitsRef.current = props.userPreferences?.units || 'imperial' }, [props.userPreferences])

  function formatLength(meters) {
    if (unitsRef.current === 'imperial') {
      const yards = meters * 1.09361
      if (yards < 1760) return `${Math.round(yards).toLocaleString()} yards`
      return `${(yards / 1760).toFixed(2)} mi`
    }
    if (meters < 1000) return `${Math.round(meters).toLocaleString()} m`
    return `${(meters / 1000).toFixed(2)} km`
  }

  function buildInfoContent(lenStr, gainStr) {
    const wrap = document.createElement('div')
    wrap.style.cssText = 'padding:10px;width:200px;background:rgba(0,0,0,0.6);border-radius:4px'

    const header = document.createElement('div')
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px'

    const title = document.createElement('p')
    title.style.cssText = 'font-size:14px;color:#589500;font-weight:500;margin:0'
    title.textContent = nameRef.current

    const closeBtn = document.createElement('a')
    closeBtn.style.cssText = 'cursor:pointer;color:white;font-size:18px;line-height:1;margin-left:8px;flex-shrink:0'
    closeBtn.textContent = '×'
    closeBtn.addEventListener('click', e => {
      e.stopPropagation()
      if (infoPopupRef.current) { infoPopupRef.current.remove(); infoPopupRef.current = null }
    })

    header.appendChild(title)
    header.appendChild(closeBtn)
    wrap.appendChild(header)

    const lenP = document.createElement('p')
    lenP.style.cssText = 'font-size:12px;color:white;margin:0'
    lenP.textContent = `Length: ${lenStr}`
    wrap.appendChild(lenP)

    const gainP = document.createElement('p')
    gainP.style.cssText = 'font-size:12px;color:white;margin:2px 0 0'
    gainP.textContent = `Elevation Gain: ${gainStr}`
    wrap.appendChild(gainP)

    return wrap
  }

  function showInfoPopup(coords, map, lenStr, gainStr) {
    const last = coords[coords.length - 1]
    if (!infoPopupRef.current) {
      infoPopupRef.current = new maptilersdk.Popup({ closeButton: false, closeOnClick: false, className: 'bm-line-info-popup', offset: 10 })
    }
    infoPopupRef.current.setLngLat([last.lng, last.lat]).setDOMContent(buildInfoContent(lenStr, gainStr)).addTo(map)
  }

  function refreshInfoPopup(coords, map) {
    if (coords.length < 2) return
    const lngLatCoords = coords.map(p => [p.lng, p.lat])
    const lenStr = formatLength(segmentsLength(lngLatCoords))
    const units = unitsRef.current
    showInfoPopup(coords, map, lenStr, '...')
    Promise.all(coords.map(p => {
      const key = `${p.lat},${p.lng}`
      if (elevationCache.current[key] !== undefined) return Promise.resolve(elevationCache.current[key])
      return elevation.getElevation(p.lat, p.lng, tokenRef.current)
        .then(e => { elevationCache.current[key] = e; return e })
        .catch(() => 0)
    })).then(eles => {
      let gain = 0
      for (let i = 1; i < eles.length; i++) if (eles[i] > eles[i - 1]) gain += eles[i] - eles[i - 1]
      const scale = units === 'imperial' ? 3.28084 : 1
      const unitLabel = units === 'imperial' ? 'ft' : 'm'
      const gainStr = `${Math.round(gain * scale).toLocaleString()} ${unitLabel}`
      if (infoPopupRef.current?.isOpen()) showInfoPopup(activeCoords.current, map, lenStr, gainStr)
    }).catch(() => {})
  }

  useEffect(() => {
    const map = props.mapReferenceThreed
    if (!map) return

    const handleClick = (e) => {
      const { lat, lng } = e.lngLat
      const newPoint = { lat, lng, altitude: 0 }

      if (!drawing.current) {
        drawing.current = true
        activeCoords.current = [newPoint]

        const el = document.createElement('div')
        el.innerHTML = `<svg viewBox="0 0 12 12" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
          <circle cx="6" cy="6" r="5" fill="${colorRef.current}" stroke="white" stroke-width="1.5"/>
        </svg>`
        markerRef.current = new maptilersdk.Marker({ element: el, anchor: 'center', pitchAlignment: 'viewport', rotationAlignment: 'viewport' })
          .setLngLat([lng, lat])
          .addTo(map)
      } else {
        activeCoords.current.push(newPoint)
        if (markerRef.current) markerRef.current.setLngLat([lng, lat])
      }

      updatePreview(map)
      refreshInfoPopup(activeCoords.current, map)
    }

    const handleDblClick = (e) => {
      e.preventDefault()
      drawing.current = false
      if (infoPopupRef.current) { infoPopupRef.current.remove(); infoPopupRef.current = null }
    }

    const addPreview = () => addPreviewLayer(map, colorRef.current)

    try { addPreview() } catch (_) {}
    map.on('styledata', addPreview)

    const handleMouseMove = (e) => {
      if (!drawing.current || activeCoords.current.length === 0) return
      const { lat, lng } = e.lngLat
      const src = map.getSource(PREVIEW_SRC)
      if (!src) return
      src.setData({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [...activeCoords.current.map(p => [p.lng, p.lat]), [lng, lat]] },
        properties: {}
      })
    }

    map.on('click', handleClick)
    map.on('dblclick', handleDblClick)
    map.on('mousemove', handleMouseMove)
    map.getCanvas().style.cursor = 'crosshair'

    return () => {
      map.off('click', handleClick)
      map.off('dblclick', handleDblClick)
      map.off('mousemove', handleMouseMove)
      map.off('styledata', addPreview)
      map.getCanvas().style.cursor = ''
      try {
        if (map.getLayer(PREVIEW_LAYER)) map.removeLayer(PREVIEW_LAYER)
        if (map.getSource(PREVIEW_SRC)) map.removeSource(PREVIEW_SRC)
      } catch (_) {}
      if (markerRef.current) { markerRef.current.remove(); markerRef.current = null }
      if (infoPopupRef.current) { infoPopupRef.current.remove(); infoPopupRef.current = null }
      drawing.current = false
      activeCoords.current = []
      elevationCache.current = {}
    }
  }, [props.mapReferenceThreed])

  const updatePreview = (map) => {
    const src = map.getSource(PREVIEW_SRC)
    if (!src) return
    src.setData({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: activeCoords.current.map(p => [p.lng, p.lat]) },
      properties: {}
    })
  }

  const undoLinePoint = () => {
    if (!activeCoords.current || activeCoords.current.length <= 1) return
    activeCoords.current.pop()
    const last = activeCoords.current[activeCoords.current.length - 1]
    if (last && markerRef.current) markerRef.current.setLngLat([last.lng, last.lat])
    const map = props.mapReferenceThreed
    if (map) updatePreview(map)
    if (activeCoords.current.length === 0) {
      drawing.current = false
      if (infoPopupRef.current) { infoPopupRef.current.remove(); infoPopupRef.current = null }
    } else if (activeCoords.current.length >= 2 && map) {
      refreshInfoPopup(activeCoords.current, map)
    }
  }

  const dismissInfoPopup = () => {
    if (infoPopupRef.current) { infoPopupRef.current.remove(); infoPopupRef.current = null }
  }

  const captureMapScreenshot = () => {
    const map = props.mapReferenceThreed
    if (!map || typeof map.getCanvas !== 'function') return Promise.resolve(null)
    // WebGL framebuffers are cleared after each frame — toBlob() must be called
    // inside a 'render' event handler while the frame is still in the backbuffer.
    return new Promise((resolve) => {
      try {
        map.once('render', () => {
          try {
            const srcCanvas = map.getCanvas()
            const cropSize = 350
            const sx = Math.max(0, (srcCanvas.width  - cropSize) / 2)
            const sy = Math.max(0, (srcCanvas.height - cropSize) / 2)
            const sw = Math.min(cropSize, srcCanvas.width)
            const sh = Math.min(cropSize, srcCanvas.height)
            const dst = document.createElement('canvas')
            dst.width = cropSize; dst.height = cropSize
            dst.getContext('2d').drawImage(srcCanvas, sx, sy, sw, sh, 0, 0, sw, sh)
            dst.toBlob(blob => resolve(blob || null), 'image/png')
          } catch { resolve(null) }
        })
        map.triggerRepaint()
      } catch { resolve(null) }
    })
  }

  const onCreateLine = () => {
    if (activeCoords.current.length < 2) return
    const geoJson = JSON.stringify({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: activeCoords.current.map(p => [p.lng, p.lat]) },
        properties: { color }
      }]
    })
    setloading(true)
    dismissInfoPopup()

    // Screenshot while the preview layer is still on the map so the line is visible.
    // The preview is removed after the capture, before lineCreateRequest.
    captureMapScreenshot()
      .then(blob => {
        // Now safe to remove the preview layer
        const map = props.mapReferenceThreed
        if (map && typeof map.getCenter === 'function') {
          try { if (map.getLayer(PREVIEW_LAYER)) map.removeLayer(PREVIEW_LAYER) } catch {}
          try { if (map.getSource(PREVIEW_SRC)) map.removeSource(PREVIEW_SRC) } catch {}
        }
        if (!blob) return Promise.resolve(null)
        const data = new FormData()
        data.append('photo', blob)
        data.append('photoInfo', JSON.stringify({ typeId: 4, name: 'map-screenshot' }))
        return props.photoUploadRequest(data).catch(() => null)
      })
      .then(photoResponse => {
        const payload = {
          name, notes, userId: props.user.id, geoJson,
          measurementTypeId: MEASUREMENT_TYPES.LINE, public: false, measureType: 'line',
          photoId: photoResponse?.id ?? ''
        }
        return props.lineCreateRequest(payload)
      })
      .then(() => { setloading(false); props.collapseLeftToolbar() })
      .catch(err => {
        // Keep the panel open so the line can be saved again (WEB-7164). The preview
        // layer was removed for the screenshot, so put it and the length popup back -
        // unless the tool has already unmounted, which clears activeCoords.
        console.error('Error saving line:', err)
        const map = props.mapReferenceThreed
        if (map && typeof map.getCenter === 'function' && activeCoords.current.length >= 2) {
          try { addPreviewLayer(map, colorRef.current); updatePreview(map) } catch { /* map torn down */ }
          refreshInfoPopup(activeCoords.current, map)
        }
        setloading(false)
        toaster.baseMapError('Couldn\'t save your line. Please try again.')
      })
  }

  function GetColorSelection() {
    const colors = ['#0000FF', '#90FF00', '#589500', '#FFFF00', '#FF6800', '#FF0000']
    return (
      <div>
        <div>LINE COLOR</div>
        <div style={{ display: 'flex', marginTop: 10, marginBottom: 10 }}>
          {colors.map((c) => {
            const active = color.toUpperCase() === c.toUpperCase()
            return (
              <div
                key={c}
                onClick={() => {
                  setColor(c)
                  colorRef.current = c
                  const map = props.mapReferenceThreed
                  if (map && map.getLayer(PREVIEW_LAYER)) {
                    map.setPaintProperty(PREVIEW_LAYER, 'line-color', c)
                  }
                  if (markerRef.current) {
                    const el = markerRef.current.getElement()
                    const circle = el.querySelector('circle')
                    if (circle) circle.setAttribute('fill', c)
                  }
                }}
                className="color-option"
                style={{ backgroundColor: c, border: active ? '4px solid #c1c0c0' : '', cursor: 'pointer' }}
              >
                {active ? <div className="color-active"></div> : null}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className='tools-content'>
      <div className='left-expanded-header-container'>
        <span className='left-expanded-header'>{'Add Lines'}</span>
        <div className='default-close left-dialog-close' onClick={() => { dismissInfoPopup(); props.collapseLeftToolbar() }}></div>
      </div>

      {loading && <div className='markers-load-spinner'><div className="loader-spinner"></div></div>}

      <div key="line-name-input">
        <label htmlFor='name' style={{ fontSize: '12px', textTransform: 'capitalize' }}>Name : </label>
        <input type='text' name='name' id='name' className='default-name-textfield'
          style={{ marginTop: '5px' }} placeholder='Name' value={name}
          onChange={(e) => setName(e.target.value)} />
      </div>

      <div style={{ marginTop: 20, marginBottom: 20 }}>
        <GetColorSelection />
      </div>

      <div style={{ marginTop: '10px', marginBottom: '10px' }}>
        <label htmlFor='notes' style={{ fontSize: '12px' }}>Notes:
          <textarea name='shapeNotes' id='shapeNotes' className='default-name-textfield'
            value={notes} onChange={(e) => setnotes(e.target.value)} placeholder=' '
            style={{ height: 100, padding: 10 }} />
        </label>
      </div>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <button style={{ width: '32%', margin: '0', backgroundColor: 'grey' }}
          onClick={() => { dismissInfoPopup(); props.collapseLeftToolbar() }}>Cancel</button>
        <button style={{ width: '32%', margin: '0', backgroundColor: 'orange' }}
          onClick={undoLinePoint}>Undo</button>
        <button id='save-button' onClick={onCreateLine}
          style={{ width: '32%', margin: '0', backgroundColor: '#589500', cursor: 'pointer' }}>
          Save Changes
        </button>
      </div>
    </div>
  )
}

const mapStateToProps = state => ({
  lines: state.lines,
  mapReferenceThreed: state.mapReferenceThreed,
  user: state.user,
  token: state.auth.token,
  userPreferences: state.userPreferences
})

const mapDispatchToProps = dispatch => ({
  lineCreateRequest: (payload) => dispatch(lineCreateRequest(payload)),
  collapseLeftToolbar: () => dispatch(collapseLeftToolbar()),
  photoUploadRequest: (data) => dispatch(photoUploadRequest(data)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ThreeDLineDetails)
